import { inject, Injectable } from '@angular/core';
import { CustomerListConfigService, Customer, MetadataService } from 'gung-standard';
import { ConfigBaseFilter, ListLayout, ListLayoutSingleComponent, SimpleConfigBaseFilter } from 'gung-list';
import { HlCustomerListCardComponent } from '../components/hl-customer-list-card/hl-customer-list-card.component';
import { first, forkJoin, map, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { CustomerListSettingsGroup, HlDisplayPortalFeaturesService } from './hl-display-portal-features.service';
import { HlSalesPersonFilter, HlSegmentFilter } from './filters/hl-display-sales-filters';

@Injectable({
  providedIn: 'root'
})
export class CustomerFinancialGroupFilter extends SimpleConfigBaseFilter<Customer> {
  protected metadataService = inject(MetadataService);

  getName(): string {
    return 'FINANCIAL_GROUP';
  }

  getOptionIds(item: Customer): string[] {
    const metadataValue = this.metadataService.getMetadataValue(
      'q_financialgrp',
      'q_financialgrpdesc',
      item.extra.kus.q_financialgrpcode
    );
    if (metadataValue == null) {
      return [];
    } else {
      return [metadataValue];
    }
  }

  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CustomerSuperGroupFilter extends SimpleConfigBaseFilter<Customer> {
  protected metadataService = inject(MetadataService);

  getName(): string {
    return 'SUPER_GROUP';
  }

  getOptionIds(item: Customer): string[] {
    const metadataValue = this.metadataService.getMetadataValue(
      'q_supergrp',
      'q_supergrpdesc',
      item.extra.kus.q_supergrpcode
    );
    if (metadataValue == null) {
      return [];
    } else {
      return [metadataValue];
    }
  }

  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CustomerMeetingFilter extends SimpleConfigBaseFilter<Customer> {
  getName(): string {
    return 'MEETING';
  }

  getOptionIds(item: Customer): string[] {
    if (item.extra.meetings?.meetingLastTwelveMonths) {
      return ['MEETING_LAST_12_MONTHS'];
    } else if (item.extra.meetings?.meetingOlderThanTwelveMonths) {
      return ['MEETING_OLDER_THAN_12_MONTHS'];
    } else {
      return ['NO_MEETING'];
    }
  }

  getOptionName(key: string): string {
    return key;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CustomerCityFilter extends SimpleConfigBaseFilter<Customer> {
  getName(): string {
    return 'CITY';
  }

  getOptionIds(item: Customer): string[] {
    return [item.extra.fr.ftgpostadr3];
  }

  getOptionName(key: string): string {
    return key.trim();
  }
}

interface CustomerListFilterGating {

  customerFiltersEnabled: boolean;
  salesPersonFilterEnabled: boolean;

  segmentFilterEnabled: boolean;

  isSalesOrCustomerService: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class HlCustomerListConfigService extends CustomerListConfigService {
  public isCustomerService: boolean = false;

  protected customerSuperGroupFilter = inject(CustomerSuperGroupFilter);
  protected financialGroupFilter = inject(CustomerFinancialGroupFilter);
  protected customerMeetingFilter = inject(CustomerMeetingFilter);
  protected customerCityFilter = inject(CustomerCityFilter);
  protected hlDisplayPortalFeaturesService = inject(HlDisplayPortalFeaturesService);
  protected metadataService = inject(MetadataService);

  protected salesPersonFilter = new HlSalesPersonFilter<Customer>(
    this.metadataService,
    item => item.extra.kus?.saljare
  );

  protected segmentFilter = new HlSegmentFilter<Customer>(item => item.extra.kus?.q_hl_segmentation);

  topFilter = false;

  protected filterGating?: CustomerListFilterGating;

  getFilters(): ConfigBaseFilter<Customer>[] {
    const gating = this.filterGating;

    if (!gating) {
      console.warn(
        'HlCustomerListConfigService.getFilters() ran before getItems() resolved the portal settings and user roles; rendering no customer filters.'
      );
      return [];
    }

    if (!gating.customerFiltersEnabled) {
      return [];
    }

    const filters: ConfigBaseFilter<Customer>[] = [
      this.financialGroupFilter,
      this.customerSuperGroupFilter,
      this.customerCityFilter,
      this.customerMeetingFilter
    ];

    if (gating.salesPersonFilterEnabled && gating.isSalesOrCustomerService) {
      filters.push(this.salesPersonFilter);
    }

    if (gating.segmentFilterEnabled && gating.isSalesOrCustomerService) {
      filters.push(this.segmentFilter);
    }

    return filters;
  }

  getLayouts(): ListLayout<Customer>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => HlCustomerListCardComponent,
        getListLayoutComponent: () => ListLayoutSingleComponent,
        getName: () => 'Customer list'
      }
    ];
  }

  getSearchTerms(item: Customer): string[] {
    const terms = [];
    if (item.id) {
      terms.push(item.id);
    }
    if (item.name) {
      terms.push(item.name);
    }
    if (item.extra.fr.ftgpostadr2) {
      terms.push(item.extra.fr.ftgpostadr2);
    }
    if (item.extra.fr.ftgpostadr3) {
      terms.push(item.extra.fr.ftgpostadr3);
    }
    if (item.extra.fr.ftgpostnr) {
      terms.push(item.extra.fr.ftgpostnr);
    }
    if (item.extra?.fr?.q_siren) {
      terms.push(item.extra.fr.q_siren);
    }
    if (item.extra?.fr?.ftgpostadr1) {
      terms.push(item.extra.fr.ftgpostadr1);
    }
    return terms;
  }

  hideZeroOptions() {
    return true;
  }

  getItems(): Observable<Customer[]> {
    return forkJoin({
      customers: super.getItems().pipe(first()),
      customerListSettings: this.hlDisplayPortalFeaturesService.getPortalFeatureGroup('customerListSettingsGroup').pipe(
        first(),
        map(group => group as CustomerListSettingsGroup)
      ),
      isCustomerService: this.authService.getHasRoles('CUSTOMER_SERVICE').pipe(first()),
      roles: this.authService.getRoles().pipe(first())
    }).pipe(
      map(({ customers, customerListSettings, isCustomerService, roles }) => {
        this.isCustomerService = isCustomerService;
        this.filterGating = {
          customerFiltersEnabled: !!customerListSettings.customerFiltersEnabled,
          // Defaults true on the backend; treat an absent field as enabled so a
          // frontend-before-backend deploy does not silently drop the filter.
          salesPersonFilterEnabled: customerListSettings.salesPersonFilterEnabled !== false,
          segmentFilterEnabled: customerListSettings.segmentFilterEnabled !== false,
          isSalesOrCustomerService: roles.includes('SALES') || roles.includes('CUSTOMER_SERVICE')
        };
        if (customerListSettings.podShowOnlyDeliveryCustomers) {
          return customers.filter(c => c.id.includes('@'));
        }

        if (this.isCustomerService) {
          return customers.filter(c => !c.id.includes('@'));
        }
        return customers;
      })
    );
  }

  getSearchGroupCss(): string {
    if (this.isCustomerService) {
      return 'customer-list-search-group';
    }

    return '';
  }
}
